import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Button, Pill } from "../../components/common/Primitives";
import {
  FileText, Download, Eye, EyeOff, Archive, Sparkles, ShieldAlert,
  ChevronRight, Loader2, FilePlus2,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const LABEL_TONE = {
  Excellent: "success", Strong: "success", Emerging: "gold", Adequate: "gold",
  Weak: "warn", Concerning: "danger", "Insufficient Data": "muted",
};

const VERDICT_TONE = {
  PROCEED: "success", "PROCEED WITH DILIGENCE": "gold", REVIEW: "warn", HOLD: "danger",
};

const REPORT_LABEL = {
  health_check: "Basic Health Check",
  detailed_dd: "Detailed Due Diligence",
};


// ---------------------------------------------------------------------------
// LIST — all reports across all startups
// ---------------------------------------------------------------------------
export function AdminDDReportsList() {
  const [items, setItems] = useState([]);
  const [reportType, setReportType] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (reportType) q.set("report_type", reportType);
      if (includeArchived) q.set("include_archived", "true");
      const { data } = await api.get(`/admin/dd-reports?${q.toString()}`);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [reportType, includeArchived]);

  return (
    <DashboardShell title="Diligence Reports" subtitle="ADMIN · REPORT VAULT" actions={
      <Link to="/admin/company-intelligence" className="text-sm text-[#8A6600] hover:underline" data-testid="admin-dd-back-link">
        Company Intelligence →
      </Link>
    }>
      <Toaster richColors position="top-right" />
      <div className="flex items-center gap-3 mb-4">
        <select
          value={reportType}
          onChange={(e) => setReportType(e.target.value)}
          className="border border-navy/15 rounded-md px-3 py-1.5 text-sm font-heading"
          data-testid="admin-dd-type-filter"
        >
          <option value="">All types</option>
          <option value="health_check">Basic Health Check</option>
          <option value="detailed_dd">Detailed Due Diligence</option>
        </select>
        <label className="flex items-center gap-2 text-xs text-ink-muted font-mono cursor-pointer">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            data-testid="admin-dd-include-archived"
          />
          Include archived
        </label>
        <div className="text-xs font-mono text-ink-muted ml-auto">{items.length} reports</div>
      </div>

      <Card testId="admin-dd-table-card" className="p-0 overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-dd-table">
          <thead className="bg-navy/[0.04] border-b border-navy/10">
            <tr>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">Startup</th>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">Report</th>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">Health</th>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">Verdict</th>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">Investor</th>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">Generated</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-ink-muted"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</td></tr>
            )}
            {!loading && items.map((it) => (
              <tr key={it.report_id} className="border-b border-navy/[0.06] hover:bg-navy/[0.02]" data-testid={`admin-dd-row-${it.report_id}`}>
                <td className="px-4 py-3">
                  <div className="font-heading text-ink">{it.startup_name || "—"}</div>
                  <div className="text-[10px] text-ink-muted font-mono">{it.startup_id}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-ink">{REPORT_LABEL[it.report_type] || it.report_type}</div>
                  <div className="text-[10px] text-ink-muted font-mono">{it.report_id}</div>
                </td>
                <td className="px-4 py-3">
                  <Pill tone={LABEL_TONE[it.label] || "muted"}>{it.label || "—"}</Pill>
                  {it.overall_score != null && <span className="ml-2 font-mono text-xs">{it.overall_score}</span>}
                </td>
                <td className="px-4 py-3">
                  <Pill tone={VERDICT_TONE[it.verdict] || "muted"}>{it.verdict || "—"}</Pill>
                </td>
                <td className="px-4 py-3">
                  {it.investor_visible
                    ? <Pill tone="success"><Eye size={10} className="inline mr-0.5" /> Visible</Pill>
                    : <Pill tone="muted"><EyeOff size={10} className="inline mr-0.5" /> Hidden</Pill>}
                  {it.archived && <Pill tone="danger" className="ml-1">Archived</Pill>}
                </td>
                <td className="px-4 py-3 text-xs text-ink-muted">
                  {it.generated_at?.slice(0, 16).replace("T", " ")}
                  <div className="text-[10px]">by {it.generated_by}</div>
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/admin/dd-reports/${it.startup_id}/${it.report_id}`}
                    className="text-[#8A6600] font-heading text-sm hover:underline"
                    data-testid={`admin-dd-open-${it.report_id}`}
                  >Open <ChevronRight size={12} className="inline" /></Link>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-ink-muted">
                No reports yet. Generate one from a startup&apos;s <Link to="/admin/financial-review" className="text-[#8A6600] hover:underline">Financial Review</Link> page.
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </DashboardShell>
  );
}


// ---------------------------------------------------------------------------
// DETAIL — single report viewer + admin controls
// ---------------------------------------------------------------------------
export function AdminDDReportDetail() {
  const { startupId, reportId } = useParams();
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/dd-reports/${startupId}/${reportId}`);
      setReport(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [startupId, reportId]);

  const toggleVisibility = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/dd-reports/${startupId}/${reportId}/toggle-visibility`,
        { investor_visible: !report.investor_visible },
      );
      toast.success(data.investor_visible ? "Report is now visible to engaged investors." : "Report hidden from investors.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const archive = async () => {
    if (!confirm("Archive this report? Investors will lose access even if visibility was on.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/dd-reports/${startupId}/${reportId}/archive`);
      toast.success("Report archived.");
      nav("/admin/dd-reports");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/admin/dd-reports/${startupId}/${reportId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.report_type}-${reportId.slice(-8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!report) return (
    <DashboardShell title="Report">
      <div className="text-ink-muted"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>
    </DashboardShell>
  );

  return (
    <DashboardShell
      title={`${REPORT_LABEL[report.report_type]} · ${report.startup_name}`}
      subtitle={`REPORT ID ${report.report_id}`}
      actions={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={downloadPdf} data-testid="admin-dd-download-pdf">
            <Download size={12} className="inline mr-1" /> PDF
          </Button>
          <Button variant={report.investor_visible ? "primary" : "ghost"} onClick={toggleVisibility} disabled={busy} data-testid="admin-dd-toggle-visibility">
            {report.investor_visible ? <><Eye size={12} className="inline mr-1" /> Visible</> : <><EyeOff size={12} className="inline mr-1" /> Hidden</>}
          </Button>
          <Button variant="ghost" onClick={archive} disabled={busy} data-testid="admin-dd-archive"><Archive size={12} className="inline mr-1" /> Archive</Button>
          <Link to="/admin/dd-reports" className="text-sm text-[#8A6600] hover:underline self-center">← Back</Link>
        </div>
      }
    >
      <Toaster richColors position="top-right" />
      <ReportView report={report.data} />
    </DashboardShell>
  );
}


// ---------------------------------------------------------------------------
// GENERATOR — button-based UI to create a new report for a startup
// ---------------------------------------------------------------------------
export function AdminDDReportGenerator({ startupId, onGenerated }) {
  const [busy, setBusy] = useState(false);

  const generate = async (report_type) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/dd-reports/${startupId}/generate`, { report_type });
      toast.success(`${REPORT_LABEL[report_type]} generated. Verdict: ${data.report.verdict}.`);
      if (onGenerated) onGenerated(data.report);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card testId="admin-dd-generator-card">
      <div className="overline mb-3 flex items-center gap-2"><FilePlus2 size={14} /> Generate DD Report</div>
      <div className="text-xs text-ink-muted mb-4">
        Freeze the current company profile, financial data, and red flags into a shareable diligence report.
        Reports are hidden from investors by default until you toggle visibility.
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="primary"
          onClick={() => generate("health_check")}
          disabled={busy}
          data-testid="admin-dd-gen-health-check"
          className="flex-1"
        >
          {busy ? <Loader2 className="animate-spin inline mr-1" size={12} /> : <Sparkles size={12} className="inline mr-1" />}
          Basic Health Check
        </Button>
        <Button
          variant="primary"
          onClick={() => generate("detailed_dd")}
          disabled={busy}
          data-testid="admin-dd-gen-detailed"
          className="flex-1"
        >
          {busy ? <Loader2 className="animate-spin inline mr-1" size={12} /> : <ShieldAlert size={12} className="inline mr-1" />}
          Detailed Due Diligence
        </Button>
      </div>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// Shared report renderer (works for admin + investor views)
// ---------------------------------------------------------------------------
export function ReportView({ report }) {
  if (!report) return null;
  const h = report.health_summary || {};
  const c = report.company_summary || {};
  const fh = report.financial_highlights || {};
  const rec = report.recommendation || {};

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <Card testId="dd-recommendation-card">
          <div className="overline mb-2">Recommendation</div>
          <div className="flex items-center gap-3 mb-2">
            <Pill tone={VERDICT_TONE[rec.verdict] || "muted"} className="text-base">{rec.verdict}</Pill>
            <div className="text-xs font-mono text-ink-muted">
              Generated {report.generated_at?.slice(0, 10)}
            </div>
          </div>
          <div className="text-sm text-ink leading-relaxed">{rec.rationale}</div>
        </Card>

        <Card testId="dd-company-card">
          <div className="overline mb-3 flex items-center gap-2"><FileText size={14} /> Company Snapshot</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <Row k="Legal Name" v={c.legal_name} />
            <Row k="CIN" v={c.cin} mono />
            <Row k="Date of Incorporation" v={c.date_of_incorporation} />
            <Row k="Status" v={`${c.company_status || "—"} · ${c.company_class || "—"}`} />
            <Row k="RoC" v={c.roc_jurisdiction} />
            <Row k="Authorised Capital" v={inr(c.authorised_capital_inr)} />
            <Row k="Paid-up Capital" v={inr(c.paid_up_capital_inr)} />
            <Row k="Listing" v={c.listing_status} />
            <Row k="Last AGM" v={c.last_agm_date} />
            <Row k="Directors / Charges" v={`${c.directors_count ?? 0} / ${c.charges_count ?? 0}`} />
            <Row k="Verification" v={(c.verification_status || "—").replaceAll("_", " ")} />
            {c.mismatch_count > 0 && <Row k="Mismatches" v={`${c.mismatch_count} flagged`} tone="danger" />}
          </div>
        </Card>

        <Card testId="dd-financials-card">
          <div className="overline mb-3">Key Financial Indicators</div>
          {Object.keys(fh).filter((k) => !k.startsWith("_")).length === 0 ? (
            <div className="text-sm text-ink-muted italic">No financial data captured yet.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {Object.entries(fh)
                .filter(([k]) => !k.startsWith("_"))
                .map(([k, v]) => (
                <div key={k} className="border border-navy/[0.08] rounded-md px-3 py-2">
                  <div className="text-[10px] font-heading uppercase tracking-wider text-ink-muted">{prettyField(k)}</div>
                  <div className="font-mono text-ink mt-0.5">{fmtVal(k, v)}</div>
                </div>
              ))}
            </div>
          )}
          {Array.isArray(fh._backfilled_from) && fh._backfilled_from.length > 0 && (
            <div className="mt-3 text-[11px] text-ink-muted italic" data-testid="dd-backfilled-note">
              Backfilled from InstaFinancials: {fh._backfilled_from.join(", ")}
            </div>
          )}
        </Card>

        <HistoricalFinancials hist={report.historical_financials} />

        {report.all_financial_fields && report.all_financial_fields.length > 0 && (
          <Card testId="dd-all-fields-card">
            <div className="overline mb-3">Full Financial Ledger ({report.all_financial_fields.length} fields)</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="dd-all-fields-table">
                <thead className="border-b border-navy/10">
                  <tr>
                    <th className="text-left py-2 pr-3 font-heading uppercase tracking-wider text-ink-muted">Field</th>
                    <th className="text-right py-2 px-3 font-heading uppercase tracking-wider text-ink-muted">Value</th>
                    <th className="text-left py-2 px-3 font-heading uppercase tracking-wider text-ink-muted">Source</th>
                    <th className="text-center py-2 px-3 font-heading uppercase tracking-wider text-ink-muted">Conf</th>
                    <th className="text-center py-2 pl-3 font-heading uppercase tracking-wider text-ink-muted">Verified</th>
                  </tr>
                </thead>
                <tbody>
                  {report.all_financial_fields.map((r) => (
                    <tr key={r.field} className="border-b border-navy/[0.05]">
                      <td className="py-1.5 pr-3">{prettyField(r.field)}</td>
                      <td className="py-1.5 px-3 text-right font-mono">{fmtVal(r.field, r.value)}</td>
                      <td className="py-1.5 px-3 text-ink-muted">{(r.source || "—").replaceAll("_", " ")}</td>
                      <td className="py-1.5 px-3 text-center font-mono">{r.confidence ?? "—"}</td>
                      <td className="py-1.5 pl-3 text-center">{r.verified ? "✓" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      <div className="space-y-4">
        <Card testId="dd-scores-card">
          <div className="overline mb-3">Health Snapshot</div>
          <div className="mb-3">
            <div className="text-5xl font-display text-ink">{h.overall_health_score ?? "—"}</div>
            <Pill tone={LABEL_TONE[h.label] || "muted"}>{h.label || "Insufficient Data"}</Pill>
          </div>
          <div className="space-y-2 text-xs">
            <ScoreRow label="Investor Readiness" v={h.investor_readiness_score} />
            <ScoreRow label="Financial Hygiene" v={h.financial_hygiene_score} />
            <ScoreRow label="Compliance" v={h.compliance_score} />
          </div>
          <div className="border-t border-navy/[0.08] mt-3 pt-3 text-xs text-ink-muted">
            <div>High-severity flags: <span className="font-mono text-ink">{h.red_flag_counts?.high ?? 0}</span></div>
            <div>Medium: <span className="font-mono">{h.red_flag_counts?.medium ?? 0}</span> · Low: <span className="font-mono">{h.red_flag_counts?.low ?? 0}</span></div>
          </div>
        </Card>

        <Card testId="dd-flags-card">
          <div className="overline mb-3">{report.all_red_flags ? "All Red Flags" : "Top Red Flags"}</div>
          {(report.all_red_flags || report.top_red_flags || []).length === 0 ? (
            <div className="text-sm text-ink-muted italic">No flags raised.</div>
          ) : (
            <div className="space-y-2">
              {(report.all_red_flags || report.top_red_flags).map((f, i) => (
                <div
                  key={f.flag_id || f.code || i}
                  className="border-l-2 pl-3 py-1"
                  style={{ borderColor: f.severity === "high" ? "#DC2626" : f.severity === "medium" ? "#F59E0B" : "#9CA3AF" }}
                  data-testid={`dd-flag-${f.code || i}`}
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <Pill tone={f.severity === "high" ? "danger" : f.severity === "medium" ? "warn" : "muted"}>{f.severity}</Pill>
                    <span className="text-[10px] font-mono text-ink-muted">{f.code || f.category}</span>
                  </div>
                  <div className="text-xs text-ink">{f.message || f.description}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {report.mismatch_flags && report.mismatch_flags.length > 0 && (
          <Card testId="dd-mismatches-card">
            <div className="overline mb-3">MCA vs Founder Mismatches</div>
            <div className="space-y-2 text-xs">
              {report.mismatch_flags.map((m, i) => (
                <div key={i} className="border border-rose-500/30 bg-rose-50/40 rounded px-2 py-1.5">
                  <div className="font-heading text-ink">{prettyField(m.field)}</div>
                  <div className="text-ink-muted">MCA: <span className="font-mono">{String(m.mca_value)}</span> · Founder: <span className="font-mono">{String(m.founder_value)}</span></div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function HistoricalFinancials({ hist }) {
  if (!hist) return null;
  const cr = hist.credit_rating || {};
  const years = hist.years || [];
  return (
    <Card testId="dd-historical-card">
      <div className="overline mb-3 flex items-center gap-2">
        <FileText size={14} /> Historical Financials
        <Pill tone="muted" className="text-[10px]">MCA · via InstaFinancials</Pill>
      </div>
      {!hist.available ? (
        <div className="text-sm text-ink-muted italic" data-testid="dd-historical-unavailable">
          Historical financials not available from InstaFinancials for this CIN.
          {hist.error ? <span className="block text-[11px] mt-1">Provider note: {hist.error}</span> : null}
        </div>
      ) : (
        <>
          {(cr.rating || cr.agency) && (
            <div className="mb-4 flex items-center gap-2 flex-wrap text-xs text-ink" data-testid="dd-credit-rating">
              <span className="text-ink-muted">Credit Rating:</span>
              <Pill tone="gold">{cr.rating || "—"}</Pill>
              <span className="text-ink-muted">Agency:</span>
              <span className="font-mono">{cr.agency || "—"}</span>
              <span className="text-ink-muted">·</span>
              <span className="font-mono">As of {cr.as_of || "—"}</span>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="dd-historical-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-ink-muted border-b border-navy/10">
                  <th className="py-2 pr-3">Metric</th>
                  {years.map((y) => (
                    <th key={y.period_end} className="py-2 px-3 text-right">{(y.period_end || "").slice(0, 10)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ["Revenue", "revenue_inr"],
                  ["PAT", "pat_inr"],
                  ["PBT", "pbt_inr"],
                  ["Net Worth", "net_worth_inr"],
                  ["Borrowings", "borrowings_inr"],
                ].map(([label, key]) => (
                  <tr key={key} className="border-b border-navy/[0.05]">
                    <td className="py-1.5 pr-3 font-heading text-ink-muted">{label}</td>
                    {years.map((y) => (
                      <td key={y.period_end + key} className="py-1.5 px-3 font-mono text-ink text-right">{inr(y[key])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hist.wilful_defaulter === true && (
            <div className="mt-3 border border-rose-300 bg-rose-50 text-rose-900 text-xs px-3 py-2 rounded" data-testid="dd-wilful-defaulter">
              ⚠ Reported as wilful defaulter in MCA records.
            </div>
          )}
          {hist.employee_count && (
            <div className="mt-2 text-[11px] text-ink-muted">EPF Employee Count: <span className="font-mono">{hist.employee_count}</span></div>
          )}
        </>
      )}
    </Card>
  );
}




function Row({ k, v, mono, tone }) {
  return (
    <div className="flex justify-between items-baseline gap-3 border-b border-navy/[0.05] py-1">
      <span className="text-[11px] font-heading uppercase tracking-wider text-ink-muted">{k}</span>
      <span className={`${mono ? "font-mono" : "font-body"} text-ink text-right ${tone === "danger" ? "text-rose-700" : ""}`}>{v || "—"}</span>
    </div>
  );
}

function ScoreRow({ label, v }) {
  const value = v ?? 0;
  const color = value >= 80 ? "bg-emerald-500" : value >= 60 ? "bg-gold" : value >= 40 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div>
      <div className="flex justify-between text-xs">
        <span className="text-ink-muted">{label}</span>
        <span className="font-mono text-ink">{v ?? "—"}</span>
      </div>
      <div className="h-1 bg-navy/[0.06] rounded-full mt-0.5 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function inr(v) {
  if (v == null) return "—";
  return "₹" + Number(v).toLocaleString("en-IN");
}

function prettyField(k) {
  return String(k).replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtVal(field, v) {
  if (v == null) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") {
    if (/_pct|growth|margin/i.test(field)) return `${v.toFixed(1)}%`;
    return "₹" + v.toLocaleString("en-IN");
  }
  return String(v);
}
